import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { Button, Card } from 'antd';

import dayjs from 'dayjs';
import isEmpty from 'lodash.isempty';

import axios from '../../../axios';

import { capitalize } from '../../../helpers/common';
import { sortDates, sortNumbers, sortStrings } from '../../../helpers/filter';
import { hasPermission } from '../../../helpers/user.js';

import { DATE_FORMAT } from '../../../constants/common';
import { BLANK_FILTER_TEXT } from '../../../constants/livestock';
import messages from '../../../constants/messages';
import { NOTIFICATION_MODAL_CONFIG, NOTIFICATION_STATUS } from '../../../constants/notifications';
import { PERMISSIONS } from '../../../constants/permissions.js';
import { TABLE_CONFIG_COLUMNS, TABLE_CONFIG_KEYS } from '../../../constants/table-configurations.js';

import { setNotificationList } from '../../../redux/actions/notification';
import { fetchNotifications } from '../../../redux/actions/notifications';
import { setSelectedFilters } from '../../../redux/reducers/filters';
import { setSortedData } from '../../../redux/reducers/notifications';

import AppModal from '../../../components/AppModal';
import AppTable from '../../../components/AppTable';
import { COLUMN_SIZE } from '../../../components/AppTable/constants';
import Priority from '../../../components/PriorityTag';
import { errorToastHandler } from '../../../components/action_notifier';

import { NotificationService } from '../../../services';
import { useFilterOptions } from './useFilterOptions';

const commonColumnProperties = {
  ellipsis: true,
  sortDirections: ['ascend', 'descend']
};

const Notifications = props => {
  const dispatch = useDispatch();

  const { alerts: notifications, loading, sortedData } = useSelector(state => state.notifications);
  const { selectedFilters } = useSelector(state => state.filters);
  const user = useSelector(state => state.user);

  const [filteredInfo, setFilteredInfo] = useState({});
  const [state, setState] = useState({
    delete_modal: false,
    filters: {
      animals: [],
      farms: props?.location?.filter?.farms || [],
      geofences: [],
      labels: [],
      rules: [],
      sites: [],
      tags: []
    },
    modal_message: '',
    modal_title: '',
    modal_type: '',
    notifications: [],
    selectedNotifications: []
  });

  const getNotifications = async query => {
    try {
      await dispatch(fetchNotifications({ query })).unwrap();
    } catch (error) {
      errorToastHandler(error?.message || error?.response?.data?.message || messages.DEFAULT_ERROR);
    }
  };

  useEffect(() => {
    getNotifications();

    return () => {
      dispatch(setSelectedFilters({}));
    };
  }, []);

  useEffect(() => {
    setState(prevState => ({
      ...prevState,
      notifications,
      selectedNotifications: []
    }));

    if (notifications?.length) {
      dispatch(setSortedData(notifications));
    }
  }, [notifications]);

  useEffect(() => {
    // on "Clear all filters" set to sortedData that used for calc filter options to original set of data
    if (isEmpty(filteredInfo) && notifications?.length) {
      dispatch(setSortedData(notifications));
    }
  }, [filteredInfo]);

  const clearFilters = () => {
    setFilteredInfo({});
    dispatch(setSelectedFilters({}));
  };

  const handleTableChange = (pagination, filters, sorter, { currentDataSource }) => {
    setFilteredInfo(filters);
    dispatch(setSelectedFilters(filters));
    dispatch(setSortedData(currentDataSource));
  };

  const selectRecords = selected => {
    setState(prevState => ({
      ...prevState,
      selectedNotifications: selected
    }));
  };

  const toggleModal = (modal, type) => {
    setState(state => ({
      ...state,
      [modal]: !state[modal],
      modal_type: type,
      modal_title:
        type === 'Delete'
          ? NOTIFICATION_MODAL_CONFIG.DELETE.title
          : type === 'Snooze'
            ? NOTIFICATION_MODAL_CONFIG.SNOOZE.title
            : NOTIFICATION_MODAL_CONFIG.UNSNOOZE.title,
      modal_message:
        type === 'Delete'
          ? NOTIFICATION_MODAL_CONFIG.DELETE.message
          : type === 'Snooze'
            ? NOTIFICATION_MODAL_CONFIG.SNOOZE.message
            : NOTIFICATION_MODAL_CONFIG.UNSNOOZE.message
    }));
  };

  const snoozeNotification = async id => {
    await axios.put('notifications/' + id + '/snooze');
    await getNotifications();
  };

  const clearNotification = async id => {
    await axios.put('notifications/' + id + '/clear');
    await getNotifications();

    const updatedNotificationList = notifications.filter(notif => notif.id !== id);
    dispatch(setNotificationList(updatedNotificationList));
  };

  const clearNotifications = async () => {
    await axios.put('notifications/clear', {
      ids: state.selectedNotifications
    });

    const updatedNotificationList = notifications.filter(notif => !state.selectedNotifications.includes(notif.id));
    dispatch(setNotificationList(updatedNotificationList));
    setState({ selectedNotifications: [] });
    await getNotifications();
  };

  const bulkOperations = async () => {
    await axios.put('notifications/snooze', {
      ids: state.selectedNotifications,
      force: true,
      seconds: 86400
    });

    setState({ delete_modal: false, selectedNotifications: [] });

    await getNotifications();
  };

  const clearAllNotifications = async () => {
    await NotificationService.clearAllNotifications();

    dispatch(setNotificationList([]));
    setState({ selectedNotifications: [] });
    await getNotifications();
  };

  const snoozeAllNotifications = async () => {
    await NotificationService.snoozeAllNotifications();

    setState({ selectedNotifications: [] });
    await getNotifications();
  };

  const actions = useMemo(() => {
    const availableActions = [];

    if (hasPermission([PERMISSIONS.NOTIFICATIONS.UPDATE_OWN], user.permissions)) {
      availableActions.push({
        label: (
          <Button
            color="default"
            size="small"
            variant="link"
            disabled={!state.selectedNotifications?.length}
            onClick={clearNotifications}>
            Clear notifications
          </Button>
        )
      });
    }

    if (hasPermission([PERMISSIONS.NOTIFICATIONS.UPDATE_OWN], user.permissions)) {
      availableActions.push({
        label: (
          <Button
            color="default"
            size="small"
            variant="link"
            disabled={!state.selectedNotifications?.length}
            onClick={() => toggleModal('delete_modal', 'Snooze')}>
            Snooze notifications
          </Button>
        )
      });
    }

    if (hasPermission([PERMISSIONS.NOTIFICATIONS.UPDATE_OWN], user.permissions)) {
      availableActions.push({
        label: (
          <Button color="default" size="small" variant="link" onClick={clearAllNotifications}>
            Clear all new notifications
          </Button>
        )
      });
    }

    if (hasPermission([PERMISSIONS.NOTIFICATIONS.UPDATE_OWN], user.permissions)) {
      availableActions.push({
        label: (
          <Button color="default" size="small" variant="link" onClick={snoozeAllNotifications}>
            Snooze all new notifications
          </Button>
        )
      });
    }

    availableActions.push({
      label: (
        <Button
          color="default"
          size="small"
          variant="link"
          disabled={!Object.values(selectedFilters)?.length}
          onClick={clearFilters}>
          Clear all filters
        </Button>
      )
    });

    return availableActions;
  }, [selectedFilters, state.selectedNotifications]);

  const columnFilterOptions = useFilterOptions(sortedData);

  const columns = [
    {
      ...commonColumnProperties,
      title: 'Notification ID',
      dataIndex: 'identifier',
      configColumnKey: TABLE_CONFIG_COLUMNS.ID,
      filteredValue: filteredInfo.identifier || null,
      searchable: true,
      width: COLUMN_SIZE.XS,
      sorter: (a, b) => sortStrings(a.identifier, b.identifier),
      render: (value, record) => <Link to={`notification/${record.id}`}>{record.identifier}</Link>
    },
    {
      ...commonColumnProperties,
      title: 'Notification Priority',
      dataIndex: 'priority',
      configColumnKey: TABLE_CONFIG_COLUMNS.PRIORITY,
      filteredValue: filteredInfo.priority || null,
      filters: columnFilterOptions.priority,
      width: COLUMN_SIZE.MD,
      onFilter: (value, record) => (value === BLANK_FILTER_TEXT ? !record?.priority : record?.priority === value),
      render: (value, record) => <Priority priority={record.priority} isClear={record.cleared_at}></Priority>,
      sorter: (a, b) => sortNumbers(a.priority_index, b.priority_index)
    },
    {
      ...commonColumnProperties,
      title: 'Notification Type',
      dataIndex: 'type',
      configColumnKey: TABLE_CONFIG_COLUMNS.TYPE,
      filteredValue: filteredInfo.type || null,
      filters: columnFilterOptions.type,
      width: COLUMN_SIZE.LG,
      onFilter: (value, record) => (value === BLANK_FILTER_TEXT ? !record?.type : record?.type === value),
      render: value => capitalize(value),
      sorter: (a, b) => sortStrings(a.type, b.type)
    },
    {
      ...commonColumnProperties,
      title: 'Notification Rule',
      dataIndex: 'ruleName',
      configColumnKey: TABLE_CONFIG_COLUMNS.RULE,
      filteredValue: filteredInfo.ruleName || null,
      searchable: true,
      width: COLUMN_SIZE.XXL,
      onFilter: (value, record) => record.rule?.name?.toLowerCase().includes(value.toLowerCase()),
      render: (value, record) => record?.rule?.name,
      sorter: (a, b) => sortStrings(a?.rule?.name, b?.rule?.name)
    },
    {
      ...commonColumnProperties,
      title: 'Livestock Count',
      dataIndex: 'animals',
      configColumnKey: TABLE_CONFIG_COLUMNS.LIVESTOCK_COUNT,
      filteredValue: filteredInfo.animals || null,
      searchable: true,
      width: COLUMN_SIZE.SM,
      onFilter: (value, record) => record.animals?.length === +value,
      render: (value, record) => record.animals?.length ?? 0,
      sorter: (a, b) => sortNumbers(a?.animals?.length, b?.animals?.length)
    },
    {
      ...commonColumnProperties,
      title: 'Notification Trigger Date',
      dataIndex: 'created_at',
      configColumnKey: TABLE_CONFIG_COLUMNS.NOTIFICATION_TRIGGER_DATE,
      filteredValue: filteredInfo.created_at || null,
      searchable: true,
      width: COLUMN_SIZE.XL,
      render: (value, record) => dayjs(record.created_at).format(DATE_FORMAT.DATETIME),
      sorter: (a, b) => sortDates(a.created_at, b.created_at)
    },
    {
      ...commonColumnProperties,
      title: 'Status',
      dataIndex: 'notificationStat',
      configColumnKey: TABLE_CONFIG_COLUMNS.STATUS,
      filteredValue: filteredInfo.notificationStat || null,
      filters: columnFilterOptions.status?.map(option => ({
        ...option,
        text: NOTIFICATION_STATUS[option.text.toLowerCase()]
      })),
      width: COLUMN_SIZE.SM,
      onFilter: (value, record) => (value === BLANK_FILTER_TEXT ? !record?.status : record?.status === value),
      render: (value, record) => NOTIFICATION_STATUS[record.status],
      sorter: (a, b) => {
        const strA = NOTIFICATION_STATUS[a.status];
        const strB = NOTIFICATION_STATUS[b.status];

        return sortStrings(strA, strB);
      }
    },
    {
      title: 'Snooze',
      dataIndex: 'snooze',
      configColumnKey: TABLE_CONFIG_COLUMNS.SNOOZE,
      align: 'center',
      ellipsis: true,
      noPadding: true,
      width: COLUMN_SIZE.XS,
      render: (_, record) => (
        <i onClick={() => snoozeNotification(record.id)} className={'not-action mdi mdi-alarm ' + record.status}></i>
      )
    },
    {
      title: 'Clear',
      dataIndex: 'clear',
      configColumnKey: TABLE_CONFIG_COLUMNS.CLEAR,
      align: 'center',
      ellipsis: true,
      noPadding: true,
      width: COLUMN_SIZE.XS,
      render: (_, record) => (
        <i
          onClick={() => clearNotification(record.id)}
          className={'not-action clear mdi mdi-check-circle-outline ' + !!record.cleared_at}></i>
      )
    }
  ];

  return (
    <>
      <Card>
        <AppTable
          headerClass="py-2"
          description="List of Notifications"
          searchPlaceholder="Search notifications"
          title="Notifications"
          tableConfigKey={TABLE_CONFIG_KEYS.NOTIFICATIONS}
          actions={actions}
          baseColumns={columns}
          dataSource={state.notifications}
          filterable={false}
          loading={loading}
          rowClassName={record => (record.cleared_at ? 'ant-table-row-accent' : '')}
          totalCount={state.notifications?.length}
          handleOnChange={handleTableChange}
          handleOnSelectRecords={selectRecords}
          handleSearchChange={getNotifications}
        />
      </Card>
      <AppModal
        isOpen={state.delete_modal}
        confirmButtonText={state.modal_type}
        title={state.modal_title}
        handleCancel={() => toggleModal('delete_modal')}
        handleConfirm={() => bulkOperations()}>
        <div className="py-4">{state.modal_message}</div>
      </AppModal>
    </>
  );
};

export default Notifications;
